import PropTypes from 'prop-types';
import React, { useEffect, useState, useRef } from 'react';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Routes from '../../utils/routes.js.erb';
import { authHeaders } from '../../actions';

const PlayerSelector = React.memo(({
  value,
  onChange,
  isDisabled,
  onErrorMessage,
  isLoading = false,
  placeholder,
}) => {
  const { t } = useTranslation();
  const searchTimeoutRef = useRef(null);

  const loadOptions = (inputValue, callback) => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (!inputValue.trim()) {
      callback([]);
      return;
    }

    searchTimeoutRef.current = setTimeout(() => {
      searchPlayersMutation.mutate(inputValue, {
        onSuccess: (data) => {
          callback(data);
        },
        onError: (error) => {
          onErrorMessage(t('errors.generic'));
          console.error('Error fetching players:', error);
          callback([]);
        }
      });
    }, 500);
  };

  const searchPlayersMutation = useMutation({
    mutationFn: async (searchQuery) => {
      const response = await fetch(
        Routes.api_dashboard_communication_filters_for_players_path({ q: searchQuery }),
        {
          method: 'GET',
          headers: authHeaders()
        }
      );

      if (!response.ok) {
        throw new Error('Error fetching players');
      }

      return response.json();
    }
  });

  return (
    <AsyncSelect
      name='sp-player-select'
      className='o-select--optgroup'
      isMulti={true}
      value={value}
      loadOptions={loadOptions}
      placeholder={isLoading ? t('communication.loading') : (placeholder || t('communication.startTyping'))}
      noOptionsMessage={() => t('communication.noOptions')}
      onChange={onChange}
      isDisabled={isDisabled || isLoading}
      cacheOptions={true}
      defaultOptions={false}
    />
  );
});

const Recipients = ({
  filters,
  isLoadingGroups = true,
  onAgeChange,
  onRecipientsChange,
  onErrorMessage,
  forGroups = false,
  selectedIndividuals = [],
  individualPlaceholder,
  groupPlaceholder,
}) => {
  const { t } = useTranslation();
  const [sourceGroups, setSourceGroups] = useState([]);
  const [targetAges, setTargetAges] = useState([]);
  const [targetGroups, setTargetGroups] = useState([]);
  const [targetPlayers, setTargetPlayers] = useState(selectedIndividuals);

  useEffect(() => {
    setTargetPlayers(selectedIndividuals);
  }, [selectedIndividuals]);

  useEffect(() => {
    const value = [
      {
        label: t('communication.recipientsGroups'),
        options: filters.groups?.map(g => ({ value: `group_${g.value}`, label: g.label })) || [],
      },
      {
        label: t('communication.recipientsSubgroups'),
        options: filters.subgroups?.map(g => ({ value: `subgroup_${g.value}`, label: g.label })) || [],
      },
      {
        label: t('communication.recipientsCohorts'),
        options: filters.cohorts?.map(g => ({ value: `cohort_${g.value}`, label: g.label })) || [],
      }
    ]
    setSourceGroups(value);
  }, [filters])

  const onIndividualsChange = (value) => {
    setTargetPlayers(value);
    onRecipientsChange(value);
  }

  const onGroupChange = (value) => {
    setTargetGroups(value);
    onRecipientsChange(value);
  }

  const onAgesChange = (value) => {
    setTargetAges(value);
    onAgeChange(value);
  }

  const renderIndividuals = () => {
    return (
      <div className="column u-padding-right--zero">
        <PlayerSelector
          value={targetPlayers}
          onChange={onIndividualsChange}
          isDisabled={isLoadingGroups}
          isLoading={isLoadingGroups}
          onErrorMessage={onErrorMessage}
          placeholder={individualPlaceholder}
        />
      </div>
    )
  };

  const renderGroups = () => {
    return (
      <div className="row u-padding-right--zero u-padding-left--base medium-12">
        <div className="column medium-8">
          <Select
            name="sp-group-select"
            className={'o-select--optgroup'}
            isMulti={true}
            value={targetGroups}
            onChange={onGroupChange}
            options={sourceGroups}
            placeholder={groupPlaceholder || t('communication.startTyping')}
            noOptionsMessage={() => t('communication.noOptions')}
            isDisabled={isLoadingGroups}/>
        </div>
        {renderAgeRanges()}
      </div>
    )
  };

  const renderAgeRanges = () => {
    return (
      <div className="column u-padding-left--zero u-padding-right--zero">
        <Select
          name="sp-age-select"
          className={'o-select--optgroup'}
          isMulti={true}
          value={targetAges}
          onChange={onAgesChange}
          options={filters.ages || []}
          placeholder={t('communication.ageRangesPlaceholder')}
          noOptionsMessage={() => t('communication.noOptions')}
          isDisabled={isLoadingGroups}/>
      </div>
    )
  }

  return (
    <div className="row u-margin-bottom--tiny" style={{alignItems: 'first baseline'}}>
      <label>{t('communication.recipientsLabel')}</label>
      {forGroups ? renderGroups() : renderIndividuals()}
    </div>
  )
}

Recipients.propTypes = {
  filters: PropTypes.shape({
    ages: PropTypes.array,
    cohorts: PropTypes.array,
    groups: PropTypes.array,
    subgroups: PropTypes.array,
  }),
  isLoadingGroups: PropTypes.bool.isRequired,
  forGroups: PropTypes.bool,
  onAgeChange: PropTypes.func.isRequired,
  onRecipientsChange: PropTypes.func.isRequired,
  selectedIndividuals: PropTypes.array,
  onErrorMessage: PropTypes.func.isRequired,
  individualPlaceholder: PropTypes.string,
  groupPlaceholder: PropTypes.string,
}

export default Recipients;
